// src/components/profile/SectionFeasibility.jsx
import React from 'react';
import { Badge as UIBadge } from '../ui/badge';
import { 
  Database, Settings, Users, DollarSign, CheckCircle, AlertTriangle, 
  Clock, Zap, Target, Download, Copy, ExternalLink, TrendingUp 
} from 'lucide-react';

const ProgressBar = ({ label, value, max = 100, color = "blue" }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const colorClasses = {
    blue: "bg-blue-500",
    green: "bg-green-500", 
    orange: "bg-orange-500",
    red: "bg-red-500"
  };

  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs text-gray-600 mb-1">
        <span>{label}</span>
        <span>{value}/{max}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full transition-all duration-300 ${colorClasses[color]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const DataChip = ({ type, status = "available" }) => {
  const statusColors = {
    available: "bg-green-100 text-green-800",
    partial: "bg-yellow-100 text-yellow-800", 
    missing: "bg-red-100 text-red-800"
  };

  return (
    <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
      <Database className="w-3 h-3" />
      {type}
    </div>
  );
};

const TrafficLightIndicator = ({ status, label }) => {
  const configs = {
    green: { bg: 'bg-green-500', text: 'text-green-700', bgLight: 'bg-green-50', label: 'Ready' },
    yellow: { bg: 'bg-yellow-500', text: 'text-yellow-700', bgLight: 'bg-yellow-50', label: 'Partial' },
    red: { bg: 'bg-red-500', text: 'text-red-700', bgLight: 'bg-red-50', label: 'Not Ready' }
  };
  
  const config = configs[status] || configs.yellow;
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${config.bgLight} border border-gray-200`}>
      <div className={`w-3 h-3 rounded-full ${config.bg}`}></div>
      <span className={`text-sm font-medium ${config.text}`}>{label}: {config.label}</span>
    </div>
  );
};

const EffortChip = ({ level, description }) => {
  const configs = {
    low: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Low Effort' },
    medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Medium Effort' },
    high: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertTriangle, label: 'High Effort' }
  };
  
  const config = configs[level] || configs.medium;
  const Icon = config.icon;
  
  return (
    <div className={`${config.bg} ${config.text} rounded-lg p-3 border border-gray-200`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <span className="font-medium text-sm">{config.label}</span>
      </div>
      <p className="text-xs">{description}</p>
    </div>
  );
};

const SystemBadge = ({ system, version, status = "compatible" }) => {
  const statusColors = {
    compatible: "bg-green-600",
    partial: "bg-yellow-600",
    incompatible: "bg-red-600"
  };

  return (
    <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-white text-xs font-medium ${statusColors[status]}`}>
      <Settings className="w-3 h-3" />
      {system} {version && <span className="opacity-75">v{version}</span>}
    </div>
  );
};

export default function SectionFeasibility({ 
  profileData, 
  feasibilityData,
  clientId, 
  useCaseId,
  isCustomized = false 
}) {
  const loading = !feasibilityData && useCaseId;
  
  // Get implementation data from profileData OR feasibilityData
  const implementation = profileData?.implementation || {};
  const readiness = implementation.readiness || 'DEBUG_READINESS';
  const complexity = implementation.complexity || 'DEBUG_COMPLEXITY';
  
  // DEBUG: Use feasibility data from props with DEBUG fallbacks
  const dataRequirements = feasibilityData?.requirements?.technical?.map(req => ({
    type: req.name,
    status: req.details?.includes('available') ? 'available' : 
            req.details?.includes('partial') ? 'partial' : 'missing'
  })) || [
    { type: "DEBUG: No Technical Requirements", status: "missing" },
    { type: "DEBUG: Claims Data", status: "missing" },
    { type: "DEBUG: Genomics", status: "missing" },
    { type: "DEBUG: Patient Outcomes", status: "missing" },
    { type: "DEBUG: Cost Data", status: "missing" }
  ];

  // DEBUG: System dependencies - API doesn't return these
  const systemDependencies = feasibilityData?.requirements?.system || [
    { system: "DEBUG: FHIR", version: "X.X", status: "incompatible" },
    { system: "DEBUG: HL7", version: "X.X", status: "incompatible" },
    { system: "DEBUG: Epic MyChart", status: "incompatible" },
    { system: "DEBUG: Cerner", version: "XXXX", status: "incompatible" }
  ];

  // DEBUG: Parse staffing with DEBUG fallbacks
  const staffing = feasibilityData?.requirements?.human?.map(req => {
    // Try to parse FTE numbers from the details field
    const detailsMatch = req.details?.match(/(\d+)\s+FTE/);
    const nameMatch = req.name?.match(/(\d+)\s+(.+)/);
    
    if (req.name === "Data Engineers" && detailsMatch) {
      return { role: "Data Engineers", current: 0, needed: parseInt(detailsMatch[1]) || 2 };
    }
    if (req.name === "Data Scientists") {
      return { role: "Data Scientists", current: 0, needed: 1 };
    }
    if (req.name === "Project Manager") {
      return { role: "Project Manager", current: 0, needed: 1 }; // 0.5 FTE rounds to 1
    }
    if (nameMatch) {
      return { role: nameMatch[2] || req.name, current: 0, needed: parseInt(nameMatch[1]) || 1 };
    }
    return { role: req.name, current: 0, needed: 1 };
  }) || [
    { role: "DEBUG: Data Engineer", current: 0, needed: 999 },
    { role: "DEBUG: Clinical Analyst", current: 0, needed: 999 },
    { role: "DEBUG: Project Manager", current: 0, needed: 999 },
    { role: "DEBUG: QA Specialist", current: 0, needed: 999 }
  ];

  // DEBUG: Costs - API doesn't return these
  const costs = feasibilityData?.costs || {
    oneTime: { software: 999999, implementation: 888888, training: 777777 },
    ongoing: { licenses: 666666, maintenance: 555555, support: 444444 }
  };

  // Traffic light readiness based on scores
  const getTrafficLightStatus = (score) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    return 'red';
  };

  // Use feasibilityScore from API (not overallScore)
  const feasibilityScore = feasibilityData?.feasibilityScore || 999;
  const feasibilityLevel = feasibilityData?.feasibilityLevel || 'DEBUG_LEVEL';

  const readinessIndicators = [
    { 
      status: getTrafficLightStatus(70), // No technical score in API
      label: 'Data Availability' 
    },
    { 
      status: getTrafficLightStatus(70), // No technical score in API
      label: 'System Compatibility' 
    },
    { 
      status: getTrafficLightStatus(60), // No organizational score in API
      label: 'Team Capacity' 
    },
    { 
      status: getTrafficLightStatus(60), // No compliance score in API
      label: 'Budget Approval' 
    }
  ];

  // Effort assessment based on complexity
  const effortAssessments = [
    { 
      level: complexity === 'High' || complexity === 'DEBUG_COMPLEXITY' ? 'high' : 'medium', 
      description: 'Data integration requires custom ETL pipelines and validation workflows' 
    },
    { 
      level: 'low', 
      description: 'System integration uses standard FHIR APIs with existing connectors' 
    },
    { 
      level: complexity === 'High' || complexity === 'DEBUG_COMPLEXITY' ? 'high' : 'medium', 
      description: 'Change management needs extensive training and workflow redesign' 
    },
    { 
      level: 'medium', 
      description: 'Testing and validation requires comprehensive QA across all systems' 
    }
  ];

  // Generate feasibility recommendation
  const getFeasibilityRecommendation = () => {
    if (feasibilityLevel === 'High') {
      return { status: 'Recommended', color: 'green', answer: 'Yes, proceed with confidence' };
    } else if (feasibilityLevel === 'Medium') {
      return { status: 'Conditional', color: 'yellow', answer: 'Yes, with moderate effort' };
    } else if (feasibilityLevel === 'Low') {
      return { status: 'Not Recommended', color: 'red', answer: 'Significant challenges identified' };
    }
    // DEBUG fallback
    return { status: 'DEBUG_STATUS', color: 'red', answer: 'DEBUG: No recommendation data' };
  };
  
  const recommendation = getFeasibilityRecommendation();
  
  // Calculate staffing gap
  const totalStaffingGap = staffing.reduce((sum, role) => sum + Math.max(0, role.needed - role.current), 0);
  
  // Show loading state
  if (loading) {
    return (
      <section className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-100 rounded"></div>
        </div>
      </section>
    );
  }
  
  return (
    <section id="feasibility" aria-labelledby="feasibility-title" className="scroll-mt-24 p-6">
      {/* Show customization indicator if applicable */}
      {isCustomized && (
        <div className="mb-4 px-3 py-1 bg-blue-50 border border-blue-200 rounded-lg inline-flex items-center gap-2 text-sm text-blue-700">
          <Zap className="w-4 h-4" />
          <span>Customized for Client #{clientId}</span>
        </div>
      )}

      {/* Feasibility Snapshot Strip */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 id="feasibility-title" className="text-2xl font-bold text-[#18365E] flex items-center gap-2">
              <Target className="w-6 h-6" />
              Feasibility Assessment
            </h2>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#FF6E4C] transition-colors">
                <Download className="w-4 h-4" />
                Feasibility Brief
              </button>
              <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#FF6E4C] transition-colors">
                <Copy className="w-4 h-4" />
                Share Link
              </button>
            </div>
          </div>
          
          {/* Snapshot Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Readiness Pill */}
            <div className="flex items-center gap-3">
              <div className="text-sm font-medium text-gray-600">Readiness</div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                readiness === 'High' ? 'bg-green-100 text-green-800' :
                readiness === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                readiness === 'DEBUG_READINESS' ? 'bg-red-100 text-red-800' :
                'bg-red-100 text-red-800'
              }`}>
                {readiness}
              </div>
            </div>
            
            {/* Complexity Badge */}
            <div className="flex items-center gap-3">
              <div className="text-sm font-medium text-gray-600">Complexity</div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                complexity === 'Low' ? 'bg-green-100 text-green-800' :
                complexity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                complexity === 'DEBUG_COMPLEXITY' ? 'bg-red-100 text-red-800' :
                'bg-red-100 text-red-800'
              }`}>
                {complexity}
              </div>
            </div>
            
            {/* Resource Gap */}
            <div className="flex items-center gap-3">
              <div className="text-sm font-medium text-gray-600">Resource Gap</div>
              <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                +{totalStaffingGap} FTEs needed
              </div>
            </div>
            
            {/* Overall Score */}
            <div className="flex items-center gap-3">
              <div className="text-sm font-medium text-gray-600">Overall Score</div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                feasibilityScore >= 70 ? 'bg-green-100 text-green-800' :
                feasibilityScore >= 50 ? 'bg-yellow-100 text-yellow-800' :
                feasibilityScore === 999 ? 'bg-red-100 text-red-800' :
                'bg-red-100 text-red-800'
              }`}>
                {feasibilityScore === 999 ? 'DEBUG_SCORE' : `${feasibilityScore}%`}
              </div>
            </div>
          </div>
          
          {/* Quick Answer Tile */}
          <div className={`border-l-4 rounded-lg p-4 ${
            recommendation.color === 'green' ? 'bg-green-50 border-green-500' :
            recommendation.color === 'yellow' ? 'bg-yellow-50 border-yellow-500' :
            'bg-red-50 border-red-500'
          }`}>
            <div className="flex items-start gap-3">
              <CheckCircle className={`w-5 h-5 mt-0.5 ${
                recommendation.color === 'green' ? 'text-green-600' :
                recommendation.color === 'yellow' ? 'text-yellow-600' :
                'text-red-600'
              }`} />
              <div>
                <h3 className={`font-semibold mb-1 ${
                  recommendation.color === 'green' ? 'text-green-800' :
                  recommendation.color === 'yellow' ? 'text-yellow-800' :
                  'text-red-800'
                }`}>Quick Answer: Can we implement this?</h3>
                <p className={`text-sm ${
                  recommendation.color === 'green' ? 'text-green-700' :
                  recommendation.color === 'yellow' ? 'text-yellow-700' :
                  'text-red-700'
                }`}>
                  <span className="font-medium">{recommendation.answer}.</span> {readiness} readiness, {complexity.toLowerCase()} complexity, 
                  need {totalStaffingGap} additional FTEs. Total investment: ${costs.oneTime.software === 999999 ? 'DEBUG' : '475K'} initial + ${costs.ongoing.licenses === 666666 ? 'DEBUG' : '120K'} annual.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Traffic Light Readiness Indicators */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Readiness Assessment
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {readinessIndicators.map((indicator, idx) => (
              <TrafficLightIndicator 
                key={idx}
                status={indicator.status}
                label={indicator.label}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Effort Assessment Chips */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Implementation Effort
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {effortAssessments.map((effort, idx) => (
              <EffortChip 
                key={idx}
                level={effort.level}
                description={effort.description}
              />
            ))}
          </div>
        </div>
      </div>

      {/* 3-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Data Requirements Column */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
            <Database className="w-5 h-5" />
            Data Requirements
          </h3>
          
          <div className="space-y-3 mb-4">
            {dataRequirements.map((req, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <DataChip type={req.type} status={req.status} />
              </div>
            ))}
          </div>
          
          <div className="pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600 mb-2">Data Readiness</div>
            <ProgressBar 
              label="Technical Score" 
              value={feasibilityData?.technicalScore || 999} 
              max={100} 
              color={feasibilityData?.technicalScore ? "green" : "red"}
            />
            <div className="text-xs text-gray-500">
              {feasibilityData?.requirements?.technical?.length || 'DEBUG'} requirements identified
            </div>
          </div>
        </div>

        {/* System Dependencies Column */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            System Dependencies
          </h3>
          
          <div className="space-y-3 mb-4">
            {systemDependencies.map((sys, idx) => (
              <div key={idx} className="flex items-center">
                <SystemBadge 
                  system={sys.system} 
                  version={sys.version} 
                  status={sys.status} 
                />
              </div>
            ))}
          </div>
          
          <div className="pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600 mb-2">Integration Complexity</div>
            <div className="flex items-center gap-2 text-sm">
              <div className={`w-3 h-3 rounded-full ${
                complexity === 'Low' ? 'bg-green-500' :
                complexity === 'Medium' ? 'bg-yellow-500' :
                complexity === 'DEBUG_COMPLEXITY' ? 'bg-gray-500' :
                'bg-red-500'
              }`}></div>
              <span className={`font-medium ${
                complexity === 'Low' ? 'text-green-700' :
                complexity === 'Medium' ? 'text-yellow-700' :
                complexity === 'DEBUG_COMPLEXITY' ? 'text-gray-700' :
                'text-red-700'
              }`}>{complexity === 'Low' ? 'Low Risk' : complexity === 'Medium' ? 'Medium Risk' : complexity === 'DEBUG_COMPLEXITY' ? 'DEBUG Risk' : 'High Risk'}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Standard APIs available • 6-8 week integration timeline
            </div>
          </div>
        </div>

        {/* Staffing & Costs Column */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#18365E] mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Staffing & Costs
          </h3>
          
          {/* Staffing Progress Bars */}
          <div className="mb-6">
            <div className="text-sm font-medium text-gray-700 mb-3">Team Readiness</div>
            {staffing.map((role, idx) => (
              <ProgressBar
                key={idx}
                label={role.role}
                value={role.current}
                max={role.needed}
                color={role.current >= role.needed ? "green" : role.current > 0 ? "orange" : "red"}
              />
            ))}
          </div>
          
          {/* Cost Cards */}
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">One-time Costs</span>
                <DollarSign className="w-4 h-4 text-gray-500" />
              </div>
              <div className="text-lg font-bold text-[#18365E]">
                ${costs.oneTime.software === 999999 ? 'DEBUG' : (costs.oneTime.software + costs.oneTime.implementation + costs.oneTime.training).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Software + Implementation + Training</div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Annual Ongoing</span>
                <Clock className="w-4 h-4 text-gray-500" />
              </div>
              <div className="text-lg font-bold text-[#18365E]">
                ${costs.ongoing.licenses === 666666 ? 'DEBUG' : (costs.ongoing.licenses + costs.ongoing.maintenance + costs.ongoing.support).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Licenses + Maintenance + Support</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Summary Footer */}
      <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-green-800 mb-1">Feasibility Summary</h4>
            <p className="text-green-700 text-sm">
              <span className="font-medium">{recommendation.status}.</span> {readiness} readiness with {complexity.toLowerCase()} complexity. 
              Technical score: {feasibilityData?.technicalScore || 'DEBUG'}%, Organizational score: {feasibilityData?.organizationalScore || 'DEBUG'}%, 
              Compliance score: {feasibilityData?.complianceScore || 'DEBUG'}%. Key constraint: need {totalStaffingGap} additional FTEs before Q2 start.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}